import React, { useState, useEffect } from "react";
import Modal from "@/components/ui/Modal";
import { ROLE_LABELS } from "@/constants/globalConstants";
import api from "@/lib/API";
import toast from "react-hot-toast";
import { Send } from "lucide-react";

export const LeadExecutionExpenseModal = ({ isOpen, onClose, project, user, onSuccess }) => {
   const [form, setForm] = useState({
      date: new Date().toISOString().split('T')[0],
      amount: "",
      toWhomPaid: "",
      reason: "",
      notes: ""
   });
   const [submitting, setSubmitting] = useState(false);

   useEffect(() => {
      if (isOpen) {
         setForm({
            date: new Date().toISOString().split('T')[0],
            amount: "",
            toWhomPaid: "",
            reason: "",
            notes: ""
         });
      }
   }, [isOpen]);

   const handleSubmit = async (e) => {
      e.preventDefault();
      if (!form.amount || Number(form.amount) <= 0) {
         toast.error("Please enter a valid amount");
         return;
      }
      if (!form.toWhomPaid?.trim()) {
         toast.error("Please enter to whom the amount is paid");
         return;
      }
      if (!form.reason?.trim() || form.reason.trim().length < 3) {
         toast.error("Reason for expense must be at least 3 characters long");
         return;
      }

      setSubmitting(true);
      try {
         const userName = (user?.name || `${user?.firstName || ''} ${user?.lastName || ''}`).trim() || "Verve Owner";
         const payload = {
            date: form.date,
            amount: Number(form.amount),
            toWhomPaid: form.toWhomPaid.trim(),
            reason: form.reason.trim(),
            notes: form.notes?.trim() || "",
            byWhomPaid: userName,
            paymentMethod: "cash",
            category: "project",
            relatedToProject: true,
            relatedProjectId: project?.leadId?._id || project?.leadId || project?._id,
            relatedProjectCollection: "VerveContact",
            status: "pending"
         };

         await api.post("/general-expenses", payload);
         toast.success("Project expense requested successfully");
         if (onSuccess) onSuccess();
         onClose();
      } catch (err) {
         console.error("Error creating project expense:", err);
         toast.error(err.response?.data?.message || err.message || "Failed to save project expense");
      } finally {
         setSubmitting(false);
      }
   };

   const displayUser = ((user?.name || `${user?.firstName || ''} ${user?.lastName || ''}`).trim() || "Verve Owner") + ` (${ROLE_LABELS[user?.role] || user?.role || "Owner"})`;
   const displayProject = `${project?.projectId || project?.verveId || project?.customId || project?._id || '—'} — ${project?.clientInfo?.name || project?.customerName || project?.clientName || project?.name || 'Customer'}`;

   return (
      <Modal isOpen={isOpen} onClose={onClose} title="REQUEST PROJECT EXPENSE" width="max-w-xl">
         <div className="mb-4 text-[11px] font-medium text-slate-500">
            This form is used for expenses related to project, advance paid to customer.
         </div>
         <form onSubmit={handleSubmit} className="space-y-4">
            {/* Row 1: Request by, request date */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
               <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                     Request by <span className="text-rose-500">*</span>
                  </label>
                  <input
                     type="text"
                     value={displayUser}
                     readOnly
                     className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg bg-slate-50 text-slate-600 focus:outline-none cursor-not-allowed font-medium"
                  />
               </div>
               <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                     Request date <span className="text-rose-500">*</span>
                  </label>
                  <input
                     type="date"
                     value={form.date}
                     onChange={(e) => setForm({ ...form, date: e.target.value })}
                     required
                     className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:border-brand-blue font-medium"
                  />
               </div>
            </div>

            {/* Row 2: Amount, To whom */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
               <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                     Amount <span className="text-rose-500">*</span>
                  </label>
                  <input
                     type="number"
                     step="any"
                     placeholder="0.00"
                     value={form.amount}
                     onChange={(e) => setForm({ ...form, amount: e.target.value })}
                     required
                     className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:border-brand-blue font-medium"
                  />
               </div>
               <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                     To whom <span className="text-rose-500">*</span>
                  </label>
                  <input
                     type="text"
                     placeholder="e.g. Reliance Power"
                     value={form.toWhomPaid}
                     onChange={(e) => setForm({ ...form, toWhomPaid: e.target.value })}
                     required
                     className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:border-brand-blue font-medium"
                  />
               </div>
            </div>

            {/* Row 3: Request for reason */}
            <div>
               <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Request for reason <span className="text-rose-500">*</span>
               </label>
               <input
                  type="text"
                  placeholder="e.g. Office Electricity Bill"
                  value={form.reason}
                  onChange={(e) => setForm({ ...form, reason: e.target.value })}
                  required
                  className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:border-brand-blue font-medium"
               />
            </div>

            {/* Row 4: Related to Project? */}
            <div className="space-y-2">
               <label className="block text-xs font-semibold text-slate-700">
                  Related to Project?
               </label>
               <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-not-allowed">
                     <input
                        type="radio"
                        checked={true}
                        readOnly
                        className="w-3.5 h-3.5 text-brand-blue focus:ring-brand-blue border-slate-300"
                     />
                     <span className="text-xs font-semibold text-slate-700">Yes</span>
                  </label>
               </div>
               <div className="px-3.5 py-2 bg-slate-50 border border-slate-200/80 rounded-xl text-xs font-semibold text-slate-700 flex items-center justify-between">
                  <span>{displayProject}</span>
               </div>
            </div>

            {/* Row 5: Notes */}
            <div>
               <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Notes
               </label>
               <textarea
                  rows={3}
                  placeholder="Describe the expense detail.."
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:border-brand-blue resize-none font-medium"
               />
            </div>

            {/* Footer buttons */}
            <div className="flex justify-end gap-3 pt-3 border-t border-slate-100">
               <button
                  type="button"
                  onClick={onClose}
                  disabled={submitting}
                  className="px-4 py-2 text-xs font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer"
               >
                  Cancel
               </button>
               <button
                  type="submit"
                  disabled={submitting}
                  className="flex items-center gap-1.5 px-5 py-2 text-xs font-semibold text-white bg-slate-900 hover:bg-slate-800 rounded-lg transition-all shadow-sm cursor-pointer disabled:opacity-50"
               >
                  <Send className="w-3.5 h-3.5" />
                  <span>{submitting ? "Submitting..." : "Request for Approval"}</span>
               </button>
            </div>
         </form>
      </Modal>
   );
};
